import React from 'react';
import { Home, Building, Key } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import SelectOption from '../ui/SelectOption';
import Disclaimer from '../ui/Disclaimer';
import DisclaimerContent from '../../utils/disclaimerContent';
import { useFirstStepDisclaimer } from '../../hooks/useFirstStepDisclaimer';

const PROPERTY_STATUSES = [
  { value: 'own_outright', icon: Key },
  { value: 'own_with_mortgage', icon: Home },
  { value: 'rent', icon: Building },
] as const;

type PropertyStatusFormProps = {
  formData: FormData;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function PropertyStatusForm({ formData, onSubmit }: PropertyStatusFormProps) {
  const { t } = useTranslation('wizard');
  const { shouldShow: showDisclaimer } = useFirstStepDisclaimer();

  return (
    <div id="property-status-form" className="space-y-6">
      <div className="text-center">
        <Title 
          id="property-status-title"
          name="property-status-title"
          as="h1" 
          className="mb-2"
        >
          {t('propertyStatus.title')}
        </Title>
        <Description 
          id="property-status-description"
          name="property-status-description"
          size="lg"
        >
          {t('propertyStatus.description')}
        </Description>
      </div>

      <div className="grid gap-3">
        {PROPERTY_STATUSES.map(({ value, icon }) => (
          <SelectOption
            key={value}
            id={`property-status-option-${value}`}
            name={`property-status-option-${value}`}
            icon={icon}
            label={t(`propertyStatus.options.${value}.label`)}
            description={t(`propertyStatus.options.${value}.description`)}
            onClick={() => onSubmit({ propertyStatus: value })}
          />
        ))}
      </div>

      {showDisclaimer && (
        <Disclaimer
          id="property-status-disclaimer"
          name="property-status-disclaimer"
        >
          <DisclaimerContent />
        </Disclaimer>
      )}
    </div>
  );
}